// AuthContext.js - Fixed to prevent blank screens after logout
import React, { createContext, useContext, useReducer, useEffect, useCallback, useState } from 'react';
import apiClient from './Api';

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  sessionInfo: {
    lastActivity: null,
    warningShown: false,
    stayLoggedIn: true
  }
};

// Actions
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  REFRESH_USER: 'REFRESH_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_LOADING: 'SET_LOADING',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  UPDATE_SESSION_INFO: 'UPDATE_SESSION_INFO',
  SHOW_INACTIVITY_WARNING: 'SHOW_INACTIVITY_WARNING',
  HIDE_INACTIVITY_WARNING: 'HIDE_INACTIVITY_WARNING'
};

// Reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        sessionInfo: {
          ...state.sessionInfo,
          lastActivity: new Date(),
          warningShown: false
        }
      };
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload.error
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        sessionInfo: {
          ...state.sessionInfo,
          warningShown: false
        }
      };

    case AUTH_ACTIONS.SESSION_EXPIRED:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Your session has expired due to inactivity. Please log in again.',
        sessionInfo: {
          ...state.sessionInfo,
          warningShown: false
        }
      };
    
    case AUTH_ACTIONS.REFRESH_USER:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false
      };
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload.isLoading
      };

    case AUTH_ACTIONS.UPDATE_SESSION_INFO:
      return {
        ...state,
        sessionInfo: {
          ...state.sessionInfo,
          ...action.payload
        }
      };

    case AUTH_ACTIONS.SHOW_INACTIVITY_WARNING:
      return {
        ...state,
        sessionInfo: {
          ...state.sessionInfo,
          warningShown: true
        }
      };

    case AUTH_ACTIONS.HIDE_INACTIVITY_WARNING:
      return {
        ...state,
        sessionInfo: {
          ...state.sessionInfo,
          warningShown: false
        }
      };
    
    default:
      return state;
  }
}

// Create context
const AuthContext = createContext();

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [inactivityWarning, setInactivityWarning] = useState(null);

  // Logout function (called both manually and automatically)
  const logout = useCallback(async (isSessionExpired = false) => {
    try {
      // Clear warning FIRST before any other operations
      setInactivityWarning(null);
      
      // Dispatch logout action to update state
      if (isSessionExpired) {
        dispatch({ type: AUTH_ACTIONS.SESSION_EXPIRED });
      } else {
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }

      // Call API logout
      await apiClient.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Use requestAnimationFrame to ensure state updates complete
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Use hash router path for Electron
        window.location.hash = '#/management/login';
        // Also try direct navigation as fallback
        if (window.location.pathname !== '/management/login') {
          window.location.href = '#/management/login';
        }
      }, 50);
    });
  }, []);

  // Handle inactivity warning - ONLY when authenticated
  const handleInactivityWarning = useCallback((remainingMinutes) => {
    // Double check authentication before showing warning
    if (state.isAuthenticated && apiClient.isAuthenticated()) {
      setInactivityWarning(remainingMinutes);
      dispatch({ type: AUTH_ACTIONS.SHOW_INACTIVITY_WARNING });
    }
  }, [state.isAuthenticated]);

  // Extend session (when user responds to warning)
  const extendSession = useCallback(() => {
    setInactivityWarning(null);
    dispatch({ type: AUTH_ACTIONS.HIDE_INACTIVITY_WARNING });
    
    // Force token refresh to extend session
    apiClient.forceRefreshToken().catch(error => {
      console.error('Failed to refresh token when extending session:', error);
    });
  }, []);

  // Set stay logged in preference
  const setStayLoggedIn = useCallback((stayLoggedIn) => {
    apiClient.setStayLoggedIn(stayLoggedIn);
    dispatch({ 
      type: AUTH_ACTIONS.UPDATE_SESSION_INFO, 
      payload: { stayLoggedIn } 
    });
  }, []);

  // Set up logout callback and inactivity warning in API client
  useEffect(() => {
    apiClient.setLogoutCallback(() => logout(true));
    apiClient.setInactivityWarningCallback(handleInactivityWarning);
  }, [logout, handleInactivityWarning]);

  // Login function
  const login = async (username, password, stayLoggedIn = true) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    
    try {
      const response = await apiClient.login(username, password);
      
      // Set stay logged in preference
      apiClient.setStayLoggedIn(stayLoggedIn);
      
      dispatch({ 
        type: AUTH_ACTIONS.LOGIN_SUCCESS, 
        payload: { user: response.user } 
      });
      
      return { success: true, user: response.user };
    } catch (error) {
      dispatch({ 
        type: AUTH_ACTIONS.LOGIN_FAILURE, 
        payload: { error: error.message } 
      });
      return { success: false, error: error.message };
    }
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Clear inactivity warning
  const clearInactivityWarning = () => {
    setInactivityWarning(null);
    dispatch({ type: AUTH_ACTIONS.HIDE_INACTIVITY_WARNING });
  };

  // Get session info
  const getSessionInfo = useCallback(() => {
    return apiClient.getActivityInfo();
  }, []);

  // Get token expiry info
  const getTokenInfo = useCallback(() => {
    return apiClient.getTokenExpiryInfo();
  }, []);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (apiClient.isAuthenticated()) {
        const user = apiClient.getCurrentUser();
        if (user) {
          dispatch({ 
            type: AUTH_ACTIONS.REFRESH_USER, 
            payload: { user } 
          });
        } else {
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: { isLoading: false } });
      }
    };

    checkAuth();
  }, []);

  // Clear warnings when authentication state changes
  useEffect(() => {
    if (!state.isAuthenticated) {
      setInactivityWarning(null);
    }
  }, [state.isAuthenticated]);

  // Handle page visibility change to check auth when tab becomes active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && state.isAuthenticated) {
        if (!apiClient.isAuthenticated()) {
          logout(true);
        } else {
          // Update session info when tab becomes visible
          const sessionInfo = apiClient.getActivityInfo();
          dispatch({ 
            type: AUTH_ACTIONS.UPDATE_SESSION_INFO, 
            payload: { lastActivity: sessionInfo.lastActivity } 
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [state.isAuthenticated, logout]);

  // Handle beforeunload to cleanup
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clear any active warnings when leaving the page
      setInactivityWarning(null);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      console.log('Network connection restored');
      // Verify auth status when coming back online
      if (state.isAuthenticated && !apiClient.isAuthenticated()) {
        logout(true);
      }
    };

    const handleOffline = () => {
      console.log('Network connection lost');
      // Clear any active warnings when offline
      setInactivityWarning(null);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [state.isAuthenticated, logout]);

  // Periodic session info update (every 5 minutes) - ONLY when authenticated
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const interval = setInterval(() => {
      // Double-check authentication before updating
      if (state.isAuthenticated && apiClient.isAuthenticated()) {
        const sessionInfo = apiClient.getActivityInfo();
        dispatch({ 
          type: AUTH_ACTIONS.UPDATE_SESSION_INFO, 
          payload: { lastActivity: sessionInfo.lastActivity } 
        });
      }
    }, 300000); // 5 minutes

    return () => clearInterval(interval);
  }, [state.isAuthenticated]);

  const value = {
    ...state,
    login,
    logout: () => logout(false),
    clearError,
    extendSession,
    setStayLoggedIn,
    clearInactivityWarning,
    getSessionInfo,
    getTokenInfo,
    inactivityWarning,
    hasRole: (role) => state.user && state.user.role === role,
    hasAnyRole: (roles) => state.user && roles.includes(state.user.role),
    isAdmin: () => state.user && state.user.role === 'admin',
    isUser: () => state.user && state.user.role === 'user',
    isSecretary: () => state.user && state.user.role === 'secretary',
    canManageUsers: () => state.user && state.user.role === 'admin',
    canManageBranches: () => state.user && ['admin', 'secretary'].includes(state.user.role),
    canManageCompanyVariables: () => state.user && state.user.role === 'admin',
    canManageAPIConfigurations: () => state.user && state.user.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;